import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CATEGORIES = {
  vegetables: 'Vegetables',
  fruits: 'Fruits',
  potato_onion: 'Potato & Onion (Import)',
  other: 'Other',
};

const EMPTY = {
  category: '',
  item_name: '',
  quantity_unit: '',
  district: '',
  province: '',
  pradeshiya_sabha: '',
  country: '',
};

function ItemModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial ? { ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const category = form.category;
  const showLocalSource = category === 'vegetables' || category === 'fruits';
  const showImportSource = category === 'potato_onion';

  const submit = async () => {
    if (!form.category) {
      toast.error('Category is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h3>{initial?.id ? 'Edit Item' : 'Add Item'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Category *</label>
            <select className="form-select" value={form.category} onChange={set('category')}>
              <option value="">— Select —</option>
              {Object.entries(CATEGORIES).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Item Name</label>
              <input className="form-input" value={form.item_name} onChange={set('item_name')} placeholder="e.g. Tomatoes" />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity Unit</label>
              <input className="form-input" value={form.quantity_unit} onChange={set('quantity_unit')} placeholder="kg / crate" />
            </div>
          </div>
          {showLocalSource && (
            <div>
              <h4 style={{ marginBottom: 12 }}>Local Supply Source</h4>
              <div className="grid-3">
                <div className="form-group">
                  <label className="form-label">District</label>
                  <input className="form-input" value={form.district} onChange={set('district')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Province</label>
                  <input className="form-input" value={form.province} onChange={set('province')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pradeshiya Sabha</label>
                  <input className="form-input" value={form.pradeshiya_sabha} onChange={set('pradeshiya_sabha')} />
                </div>
              </div>
            </div>
          )}
          {showImportSource && (
            <div className="form-group">
              <label className="form-label">Imported From Country</label>
              <input className="form-input" value={form.country} onChange={set('country')} placeholder="Country name" />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Items({ member }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoad] = useState(false);
  const [modal, setModal] = useState(null);
  const [deleting, setDel] = useState(null);

  useEffect(() => {
    if (!member?.id) return;
    setLoad(true);
    api.get(`/items/${member.id}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load items'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const handleSave = async form => {
    if (modal.row?.id) {
      const { data } = await api.put(`/items/${modal.row.id}`, form);
      setRows(p => p.map(r => r.id === data.id ? data : r));
      toast.success('Updated');
    } else {
      const { data } = await api.post('/items', { member_id: member.id, ...form });
      setRows(p => [data, ...p]);
      toast.success('Added');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this item?')) return;
    setDel(id);
    try {
      await api.delete(`/items/${id}`);
      setRows(p => p.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDel(null);
    }
  };

  const sourceLabel = row => {
    if (row.category === 'vegetables' || row.category === 'fruits') {
      return [row.district, row.province, row.pradeshiya_sabha].filter(Boolean).join(', ') || 'Local source not set';
    }
    if (row.category === 'potato_onion') {
      return row.country || 'Imported source not set';
    }
    return '—';
  };

  if (!member?.id) return (
    <div className="empty-state"><p>Save Personal info first to manage market items.</p></div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setModal({ row: null })}>
          + Add Item
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p>No items added yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Source</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td><span className="badge badge-blue">{CATEGORIES[row.category] || row.category}</span></td>
                  <td>{sourceLabel(row)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ row })}>Edit</button>
                      <button className="btn btn-danger btn-sm" disabled={deleting === row.id}
                        onClick={() => handleDelete(row.id)}>
                        {deleting === row.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <ItemModal initial={modal.row} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
}
